import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { products as fallbackProducts, type Product } from '../mocks/tienda';

/* ── Mappers entre el modelo de la app (Product) y la fila de Supabase ── */
export function productToDb(item: Partial<Product>): Record<string, unknown> {
  return {
    name: item.name,
    category: item.category,
    collection: item.collection,
    price: item.price,
    description: item.description,
    details: item.details ?? [],
    materials: item.materials,
    measurements: item.measurements,
    image: item.image,
    images: item.images ?? [],
    availability: item.availability,
    delivery_time: item.deliveryTime,
    stock: item.stock ?? 0,
  };
}

export function productFromDb(row: Record<string, unknown>): Product {
  return {
    id: row.id as number,
    name: row.name as string,
    category: row.category as Product['category'],
    collection: row.collection as string,
    price: row.price as number,
    description: row.description as string,
    details: (row.details as string[]) ?? [],
    materials: row.materials as string,
    measurements: row.measurements as string,
    image: row.image as string,
    images: (row.images as string[]) ?? [],
    availability: row.availability as Product['availability'],
    deliveryTime: row.delivery_time as string,
    stock: (row.stock as number) ?? 0,
  };
}

/**
 * Fuente única de productos para la tienda pública: lee la tabla `products`
 * de Supabase (la misma que edita el admin). Si Supabase falla o no devuelve
 * nada, usa el catálogo local como respaldo para no dejar la tienda vacía.
 */
export function useSupabaseProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: rows, error: err } = await supabase
          .from('products')
          .select('*')
          .eq('visible', true)
          .order('id', { ascending: true });
        if (!active) return;
        if (err) {
          setError(err.message);
          setProducts(fallbackProducts);
        } else if (rows && rows.length > 0) {
          setProducts(rows.map(productFromDb));
        } else {
          setProducts(fallbackProducts);
        }
      } catch {
        if (active) {
          setError('Error de conexión');
          setProducts(fallbackProducts);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return { products, loading, error };
}
